import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
// Removed FontAwesome icons - using simple text/HTML elements instead
import { validateCustomerDetails, validateAddress, formatPhoneNumber, validateRealEmail, validateRealPhone } from '../../../../utils/bookingValidation';
import { venposService } from '../../../../services/venposService';
import { useBooking, BOOKING_ACTIONS } from '../../../../contexts/BookingContext';
import config from '../../../../config/environment';
import { normaliseVRM, shouldShowCarRegistration } from '../../../../utils/membershipCarRegistration';

const VEHICLE_LOOKUP_SESSION_FAIL_KEY = 'belvoir_vl_fails';
const VEHICLE_LOOKUP_MAX_SESSION_FAILS = 5;

const getVehicleLookupSessionFailCount = () => {
  try {
    const raw = sessionStorage.getItem(VEHICLE_LOOKUP_SESSION_FAIL_KEY);
    const n = parseInt(raw || '0', 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
};

const incrementVehicleLookupSessionFailCount = () => {
  try {
    sessionStorage.setItem(VEHICLE_LOOKUP_SESSION_FAIL_KEY, String(getVehicleLookupSessionFailCount() + 1));
  } catch {
    /* ignore */
  }
};

const resetVehicleLookupSessionFailCount = () => {
  try {
    sessionStorage.removeItem(VEHICLE_LOOKUP_SESSION_FAIL_KEY);
  } catch {
    /* ignore */
  }
};

const VEHICLE_SERVER_BLOCK_UNTIL_KEY = 'belvoir_vl_srv_until';

const readVehicleServerBlockedUntil = () => {
  try {
    const t = parseInt(sessionStorage.getItem(VEHICLE_SERVER_BLOCK_UNTIL_KEY) || '0', 10);
    return t > Date.now() ? t : 0;
  } catch {
    return 0;
  }
};

const setVehicleServerBlockedUntilStorage = (untilMs) => {
  try {
    if (!untilMs || untilMs <= Date.now()) {
      sessionStorage.removeItem(VEHICLE_SERVER_BLOCK_UNTIL_KEY);
    } else {
      sessionStorage.setItem(VEHICLE_SERVER_BLOCK_UNTIL_KEY, String(untilMs));
    }
  } catch {
    /* ignore */
  }
};

const isVehicleSessionBlocked = () => getVehicleLookupSessionFailCount() >= VEHICLE_LOOKUP_MAX_SESSION_FAILS;

/**
 * Format a Date object as YYYY-MM-DD in local timezone
 * Avoids timezone conversion issues that occur with toISOString()
 */
const formatDateLocal = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const CustomerContainer = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 768px) {
    padding: 0 0.35rem;
  }

  @media (max-width: 480px) {
    padding: 0;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: clamp(1.35rem, 3.5vw, 1.85rem);
`;

const PageHeaderAccent = styled.div`
  width: 40px;
  height: 3px;
  margin: 0 auto 0.85rem;
  border-radius: 99px;
  background: linear-gradient(
    90deg,
    ${props => props.theme.colors.accent},
    ${props => props.theme.colors.primary}
  );
  opacity: 0.88;
`;

const SectionEyebrow = styled.span`
  display: block;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.42);
  margin-bottom: 0.45rem;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.45rem, 3.2vw, 1.85rem);
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.55rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.15;
`;

const SectionSubtitle = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.88rem, 1.75vw, 0.96rem);
  color: rgba(16, 29, 64, 0.52);
  margin: 0 auto;
  max-width: 34rem;
  line-height: 1.6;
`;

const FormSection = styled.div`
  background: #fff;
  border: 1px solid rgba(16, 29, 64, 0.08);
  border-radius: 16px;
  padding: clamp(1.15rem, 2.8vw, 1.75rem);
  margin-bottom: 1.25rem;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.92) inset,
    0 12px 36px rgba(16, 29, 64, 0.06);

  @media (max-width: 768px) {
    padding: 1.15rem;
    margin-bottom: 1.1rem;
  }

  @media (max-width: 576px) {
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 14px;
  }

  @media (max-width: 480px) {
    padding: 0.9rem;
    margin-bottom: 0.9rem;
  }
`;

const SectionHeader = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.02rem, 2.1vw, 1.14rem);
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1.05rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid rgba(16, 29, 64, 0.07);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  font-weight: 700;
  letter-spacing: -0.01em;

  @media (max-width: 576px) {
    margin-bottom: 0.95rem;
    padding-bottom: 0.75rem;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.15rem 1.35rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.05rem;
  }

  @media (max-width: 480px) {
    gap: 0.95rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 600;
  color: rgba(16, 29, 64, 0.78);
  margin-bottom: 0.4rem;
  font-size: 0.82rem;
  letter-spacing: 0.01em;
  display: block;
  word-wrap: break-word;

  @media (max-width: 576px) {
    font-size: 0.8rem;
    margin-bottom: 0.32rem;
  }

  @media (max-width: 480px) {
    font-size: 0.78rem;
  }
`;

const Required = styled.span`
  color: ${props => props.theme.colors.accent};
  margin-left: 0.25rem;
`;

const Input = styled.input`
  padding: 0.65rem 0.85rem;
  border: 1px solid ${props => props.$hasError ? 'rgba(183, 28, 28, 0.55)' : 'rgba(16, 29, 64, 0.14)'};
  border-radius: 10px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.95rem;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
  background: ${props => props.disabled ? 'rgba(16, 29, 64, 0.04)' : 'white'};
  color: ${props => props.disabled ? 'rgba(16, 29, 64, 0.45)' : 'inherit'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'text'};
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: rgba(157, 109, 73, 0.55);
    box-shadow: 0 0 0 3px rgba(157, 109, 73, 0.12);
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.secondary};
  }
  
  /* Detect autofill using animation */
  &:-webkit-autofill {
    animation-name: onAutoFillStart;
    animation-duration: 0.001s;
  }
  
  &:not(:-webkit-autofill) {
    animation-name: onAutoFillCancel;
    animation-duration: 0.001s;
  }
  
  @keyframes onAutoFillStart {
    from { opacity: 0.99; }
    to { opacity: 1; }
  }
  
  @keyframes onAutoFillCancel {
    from { opacity: 0.99; }
    to { opacity: 1; }
  }
  
  @media (max-width: 576px) {
    padding: 0.625rem;
    font-size: 0.95rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem;
    font-size: 0.9rem;
  }
`;

const TextArea = styled.textarea`
  padding: 0.65rem 0.85rem;
  border: 1px solid ${props => props.$hasError ? 'rgba(183, 28, 28, 0.55)' : 'rgba(16, 29, 64, 0.14)'};
  border-radius: 10px;
  font-size: 0.95rem;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
  background: white;
  min-height: 88px;
  resize: vertical;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: rgba(157, 109, 73, 0.55);
    box-shadow: 0 0 0 3px rgba(157, 109, 73, 0.12);
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.secondary};
  }
  
  @media (max-width: 576px) {
    padding: 0.625rem;
    font-size: 0.95rem;
    min-height: 70px;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem;
    font-size: 0.9rem;
    min-height: 60px;
  }
`;

const Select = styled.select`
  padding: 0.65rem 0.85rem;
  border: 1px solid ${props => props.$hasError ? 'rgba(183, 28, 28, 0.55)' : 'rgba(16, 29, 64, 0.14)'};
  border-radius: 10px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.95rem;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
  background: ${props => props.disabled ? 'rgba(16, 29, 64, 0.04)' : 'white'};
  color: ${props => props.disabled ? 'rgba(16, 29, 64, 0.45)' : 'inherit'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: rgba(157, 109, 73, 0.55);
    box-shadow: 0 0 0 3px rgba(157, 109, 73, 0.12);
  }
  
  @media (max-width: 576px) {
    padding: 0.625rem;
    font-size: 0.95rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem;
    font-size: 0.9rem;
  }
`;

const ErrorMessage = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #b71c1c;
  font-size: 0.78rem;
  margin-top: 0.28rem;
  line-height: 1.35;
`;

const FullWidthGroup = styled(FormGroup)`
  grid-column: 1 / -1;
`;

const InfoBox = styled.div`
  background: linear-gradient(165deg, rgba(241, 239, 234, 0.5) 0%, rgba(255, 255, 255, 0.98) 100%);
  border: 1px solid rgba(16, 29, 64, 0.1);
  border-radius: 14px;
  padding: clamp(1.1rem, 2.8vw, 1.45rem);
  margin-top: 1.5rem;
  text-align: center;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.92) inset,
    0 8px 28px rgba(16, 29, 64, 0.05);

  @media (max-width: 768px) {
    margin-top: 1.25rem;
  }

  @media (max-width: 576px) {
    margin-top: 1.1rem;
    border-radius: 12px;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    margin-top: 1rem;
  }
`;

const InfoText = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: rgba(16, 29, 64, 0.72);
  margin: 0;
  font-size: clamp(0.88rem, 1.85vw, 0.94rem);
  line-height: 1.55;
`;

const FieldHint = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: rgba(16, 29, 64, 0.46);
  margin-top: 0.28rem;
  line-height: 1.35;
`;

const RegistrationBanner = styled.div`
  background: linear-gradient(165deg, rgba(232, 245, 248, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%);
  border: 1px solid rgba(23, 162, 184, 0.35);
  border-radius: 14px;
  padding: clamp(1.1rem, 2.8vw, 1.45rem);
  margin-bottom: clamp(1.25rem, 3vw, 1.65rem);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 10px 32px rgba(16, 29, 64, 0.06);

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }

  @media (max-width: 576px) {
    border-radius: 12px;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1.1rem;
  }
`;

const RegistrationInfo = styled.div`
  flex: 1;
`;

const RegistrationTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1rem, 2.1vw, 1.12rem);
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.4rem 0;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  letter-spacing: -0.01em;
`;

const RegistrationText = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.84rem, 1.75vw, 0.9rem);
  color: rgba(16, 29, 64, 0.62);
  margin: 0;
  line-height: 1.55;
`;

const ChangeDetailsButton = styled.button`
  background: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  padding: 0.62rem 1.25rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
  white-space: nowrap;
  box-shadow: 0 4px 14px rgba(157, 109, 73, 0.22);

  &:hover {
    background: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(16, 29, 64, 0.18);
  }

  @media (max-width: 768px) {
    white-space: normal;
  }

  @media (max-width: 576px) {
    width: 100%;
    padding: 0.72rem 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

/* Belvoir account sign-in / sign-up section */
const AccountSectionComingSoonWrap = styled.div`
  position: relative;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }

  @media (max-width: 576px) {
    margin-bottom: 1.25rem;
  }
`;

const AccountSectionComingSoonOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(248, 249, 250, 0.65);
  border-radius: 12px;
  pointer-events: none;
  z-index: 1;

  @media (max-width: 576px) {
    border-radius: 10px;
  }
`;

const AccountSectionComingSoonText = styled.span`
  font-size: clamp(1.05rem, 3.2vw, 1.35rem);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.primary};
  padding: 0.5rem 1rem;
  border: 2px solid ${props => props.theme.colors.primary};
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
`;

const AccountSection = styled.div`
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border: 2px solid ${props => props.theme.colors.accent};
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 0;
  opacity: 0.45;
  filter: grayscale(0.9);
  pointer-events: none;
  user-select: none;

  @media (max-width: 768px) {
    padding: 1.25rem;
  }

  @media (max-width: 576px) {
    padding: 1rem;
    border-radius: 10px;
  }
`;

const AccountSectionTitle = styled.h3`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem 0;
  font-weight: 600;
`;

const AccountRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  
  &:last-of-type {
    margin-bottom: 0;
  }
  
  @media (max-width: 576px) {
    flex-wrap: wrap;
  }
`;

const SignInButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 0.625rem 1.25rem;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

const AccountDivider = styled.div`
  height: 1px;
  background: rgba(0,0,0,0.1);
  margin: 1rem 0;
`;

const CreateAccountCheckboxWrap = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const GuestHint = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.84rem;
  color: rgba(16, 29, 64, 0.48);
  margin: 0.65rem 0 0 0;
  line-height: 1.45;
`;

const ValidationSummary = styled.div`
  background: ${props =>
    props.$isComplete
      ? 'linear-gradient(165deg, rgba(232, 245, 233, 0.85) 0%, rgba(255, 255, 255, 0.98) 100%)'
      : 'linear-gradient(165deg, rgba(255, 248, 225, 0.75) 0%, rgba(255, 255, 255, 0.98) 100%)'};
  border: 1px solid ${props => (props.$isComplete ? 'rgba(46, 125, 50, 0.28)' : 'rgba(245, 124, 0, 0.35)')};
  border-radius: 14px;
  padding: clamp(1.1rem, 2.8vw, 1.45rem);
  margin-top: 1.5rem;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 8px 28px rgba(16, 29, 64, 0.05);

  @media (max-width: 768px) {
    margin-top: 1.25rem;
  }

  @media (max-width: 576px) {
    margin-top: 1.1rem;
    border-radius: 12px;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    margin-top: 1rem;
  }
`;

const SummaryTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.02rem, 2.1vw, 1.12rem);
  color: ${props => (props.$isComplete ? '#1b5e20' : '#bf360c')};
  margin: 0 0 0.75rem 0;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  letter-spacing: -0.01em;
`;

const MissingFieldsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const MissingFieldItem = styled.li`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: rgba(16, 29, 64, 0.62);
  font-size: clamp(0.84rem, 1.75vw, 0.91rem);
  padding: 0.38rem 0;
  padding-left: 0.85rem;
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
  line-height: 1.45;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.55em;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${props => props.theme.colors.accent};
    opacity: 0.85;
    flex-shrink: 0;
  }
`;

const CompleteMessage = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #1b5e20;
  font-size: clamp(0.9rem, 1.9vw, 0.97rem);
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  line-height: 1.45;

  &::before {
    content: '✓';
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    flex-shrink: 0;
    margin-top: 0.06em;
    font-size: 0.72rem;
    font-weight: 700;
    line-height: 1;
    color: #2e7d32;
    background: rgba(46, 125, 50, 0.12);
    border: 1px solid rgba(46, 125, 50, 0.35);
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid ${props => props.theme.colors.greyBackground};
  border-radius: 50%;
  border-top-color: ${props => props.theme.colors.accent};
  animation: spin 1s ease-in-out infinite;
  margin-left: 0.5rem;
  flex-shrink: 0; /* Prevent size changes that could trigger ResizeObserver */
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin-top: 2px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
`;

const CheckboxLabel = styled.label`
  font-size: 0.9rem;
  color: ${props => props.disabled ? '#999' : props.theme.colors.secondary};
  line-height: 1.4;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  flex: 1;
  opacity: ${props => props.disabled ? 0.5 : 1};
`;

const ValidationMessage = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: ${props =>
    props.$isSuccess ? 'rgba(232, 245, 233, 0.65)' : 'rgba(255, 235, 238, 0.65)'};
  color: ${props => (props.$isSuccess ? '#1b5e20' : '#b71c1c')};
  border: 1px solid ${props => (props.$isSuccess ? 'rgba(46, 125, 50, 0.28)' : 'rgba(183, 28, 28, 0.28)')};
  border-radius: 10px;
  padding: 0.85rem 1rem;
  margin-top: 1rem;
  font-size: 0.88rem;
  line-height: 1.45;
  transition: all 0.2s ease;
  will-change: auto;
  contain: layout;
`;

const PostcodeLookupSection = styled(FullWidthGroup)`
  background: rgba(16, 29, 64, 0.02);
  border: 1px solid rgba(16, 29, 64, 0.1);
  border-radius: 14px;
  padding: clamp(1.05rem, 2.6vw, 1.35rem);
  margin-bottom: 1.25rem;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.92) inset;

  @media (max-width: 768px) {
    margin-bottom: 1.1rem;
  }

  @media (max-width: 576px) {
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 12px;
  }

  @media (max-width: 480px) {
    padding: 0.9rem;
    margin-bottom: 0.9rem;
  }
`;

const PostcodeHeader = styled.div`
  margin-bottom: 0.85rem;
`;

const PostcodeTitle = styled.h4`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1rem, 2.05vw, 1.08rem);
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.35rem 0;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const PostcodeHint = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.82rem, 1.75vw, 0.88rem);
  color: rgba(16, 29, 64, 0.52);
  margin: 0;
  line-height: 1.45;
`;

const PostcodeInputGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  
  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const PostcodeInput = styled(Input)`
  flex: 1;
  font-size: 1rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const FindAddressButton = styled.button`
  padding: 0.65rem 1.35rem;
  background: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  border-radius: 999px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 140px;
  box-shadow: 0 4px 14px rgba(157, 109, 73, 0.22);
  justify-content: center;
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primary};
    box-shadow: 0 6px 18px rgba(16, 29, 64, 0.18);
    transform: translateY(-1px);
  }

  &:disabled {
    background: ${props => props.theme.colors.greyBackground};
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.95rem;
    min-width: 140px;
  }
  
  @media (max-width: 600px) {
    width: 100%;
    padding: 0.75rem 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.625rem 1rem;
    font-size: 0.9rem;
  }
`;

const AddressDropdownContainer = styled.div`
  margin-top: 1rem;
`;

const AddressSelect = styled(Select)`
  width: 100%;
  font-size: 1rem;
  padding: 0.75rem;
`;

const AddressHint = styled.p`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.secondary};
  margin-top: 0.5rem;
  font-style: italic;
`;

const ManualEntryHint = styled.p`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.secondary};
  margin-top: 1rem;
  text-align: center;
  font-style: italic;
`;

const VehicleCard = styled.div`
  margin-top: 0.75rem;
  padding: 1rem;
  background: #f0f7f0;
  border: 1px solid #4caf50;
  border-radius: 8px;
  font-size: 0.875rem;
`;

const VehicleCardTitle = styled.div`
  font-weight: 700;
  color: #2e7d32;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const VehicleGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.25rem 1rem;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const VehicleField = styled.div`
  display: flex;
  gap: 0.35rem;
  color: ${props => props.theme.colors.primary};
`;

const VehicleFieldLabel = styled.span`
  font-weight: 600;
  white-space: nowrap;
`;

const VehicleRegPlate = styled.div`
  display: inline-block;
  background: #f5c518;
  color: #000;
  font-weight: 800;
  font-size: 1.1rem;
  letter-spacing: 0.1em;
  padding: 0.2rem 0.75rem;
  border-radius: 4px;
  border: 2px solid #333;
  margin-bottom: 0.6rem;
  font-family: 'Charles Wright', 'UKNumberPlate', monospace;
`;

const VehicleLookupLoading = styled.div`
  margin-top: 0.75rem;
  font-size: 0.85rem;
  color: ${props => props.theme.colors.secondary};
  font-style: italic;
`;

const VehicleLookupError = styled.div`
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: #856404;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
`;

const VehicleSecurityBanner = styled.div`
  margin-bottom: 1.25rem;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  border: 1px solid #c62828;
  background: #ffebee;
  color: #b71c1c;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const VehicleSecurityBannerTitle = styled.strong`
  display: block;
  margin-bottom: 0.35rem;
  font-size: 1rem;
`;

const MemberDetailsCard = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 576px) {
    padding: 1rem;
    margin-bottom: 1.25rem;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 0.875rem;
    margin-bottom: 1rem;
  }
`;

const MemberTitle = styled.h4`
  margin-bottom: 1rem;
  color: #333;
  font-size: 1.1rem;
  
  @media (max-width: 576px) {
    font-size: 1rem;
    margin-bottom: 0.875rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin-bottom: 0.75rem;
  }
`;


const CustomerDetails = ({ customerDetails, registeredCustomerId, membershipDetails, onUpdate, onAddressUpdate, onMemberDetailsUpdate, onClearRegistration }) => {
  const { locationId, selectedTickets, selectedDate, selectedTimeSlot, pageId, dispatch } = useBooking();
  const [errors, setErrors] = useState({});
  const [memberErrors, setMemberErrors] = useState({});
  const [titles, setTitles] = useState([]);
  const [countries, setCountries] = useState([]);
  const [registrationSettings, setRegistrationSettings] = useState({});
  const [loading, setLoading] = useState({
    validation: false,
    registration: false
  });
  const [validationMessage, setValidationMessage] = useState(null);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [createAccountChecked, setCreateAccountChecked] = useState(false);
  const isRegistered = !!registeredCustomerId;
  
  // Check if this is grotto retail village page
  const GROTTO_RETAIL_VILLAGE_PAGE_ID = '9afa8eb9-dd32-4529-b9af-316e13a7397e';
  const isGrottoRetailVillage = pageId === GROTTO_RETAIL_VILLAGE_PAGE_ID;
  
  // Get questions and participant details from tickets
  const grottoQuestions = useMemo(() => {
    if (!isGrottoRetailVillage || !selectedTickets?.length) return null;
    const ticketWithQuestions = selectedTickets.find(t => t.eventQuestions && t.eventQuestions.length > 0);
    return ticketWithQuestions?.eventQuestions || null;
  }, [isGrottoRetailVillage, selectedTickets]);
  
  const grottoParticipantDetails = useMemo(() => {
    if (!isGrottoRetailVillage || !selectedTickets?.length) return null;
    const ticketWithParticipants = selectedTickets.find(t => t.participantDetails && t.participantDetails.length > 0);
    return ticketWithParticipants?.participantDetails || null;
  }, [isGrottoRetailVillage, selectedTickets]);
  
  // Get question answers and participant answers from tickets
  const [localQuestionAnswers, setLocalQuestionAnswers] = useState({});
  const [localParticipantAnswers, setLocalParticipantAnswers] = useState({});
  
  useEffect(() => {
    if (isGrottoRetailVillage && selectedTickets?.length) {
      const ticket = selectedTickets.find(t => t.questionAnswers || t.participantAnswers);
      if (ticket) {
        if (ticket.questionAnswers) setLocalQuestionAnswers(ticket.questionAnswers);
        if (ticket.participantAnswers) setLocalParticipantAnswers(ticket.participantAnswers);
      }
    }
  }, [isGrottoRetailVillage, selectedTickets]);
  
  // Handle question answer changes
  const handleQuestionAnswerChange = (questionId, occurrenceIndex, answer) => {
    const questionKey = `${questionId}__${occurrenceIndex}`;
    const newAnswers = { ...localQuestionAnswers, [questionKey]: answer };
    setLocalQuestionAnswers(newAnswers);
    
    // Update tickets with new answers
    const updatedTickets = selectedTickets.map(ticket => ({
      ...ticket,
      questionAnswers: newAnswers
    }));
    dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TICKETS, payload: updatedTickets });
  };
  
  // Handle participant answer changes
  const handleParticipantAnswerChange = (participantId, fieldName, value) => {
    const newAnswers = {
      ...localParticipantAnswers,
      [participantId]: {
        ...localParticipantAnswers[participantId],
        [fieldName]: value
      }
    };
    setLocalParticipantAnswers(newAnswers);
    
    // Update tickets with new answers
    const updatedTickets = selectedTickets.map(ticket => ({
      ...ticket,
      participantAnswers: newAnswers
    }));
    dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TICKETS, payload: updatedTickets });
  };
  
  // Build question key for duplicate questions
  const makeQuestionKey = (questionId, occurrenceIndex) => `${questionId}__${occurrenceIndex}`;
  
  // Detect if this is a membership journey
  const isMembershipJourney = useMemo(() => {
    // Check if pageId matches a membership page ID
    if (pageId === '37bd858f-4f04-4e83-a469-05274db0d546' || pageId === '9d7c060b-6088-4bc1-8def-7bddb9b87ef9') {
      return true;
    }
    // Check if any selected tickets have webSaleItemTypeID === 4 (membership type)
    if (selectedTickets && selectedTickets.length > 0) {
      return selectedTickets.some(ticket => ticket.webSaleItemTypeID === 4);
    }
    // Check if membershipDetails is provided and has data
    if (membershipDetails && Array.isArray(membershipDetails) && membershipDetails.length > 0) {
      return true;
    }
    return false;
  }, [pageId, selectedTickets, membershipDetails]);
  
  // Store member details in state - only for membership journeys
  const [localMembershipDetails, setLocalMembershipDetails] = useState(
    isMembershipJourney && membershipDetails ? membershipDetails : []
  );
  
  // Update local state when prop changes (only for membership journeys)
  useEffect(() => {
    if (isMembershipJourney && membershipDetails) {
      setLocalMembershipDetails(membershipDetails);
    } else if (!isMembershipJourney) {
      // Clear member details for non-membership journeys
      setLocalMembershipDetails([]);
    }
  }, [isMembershipJourney, membershipDetails]);
  
  // Address lookup state
  const [addressLookup, setAddressLookup] = useState({
    loading: false,
    addresses: [],
    selectedAddressId: '',
    error: null
  });

  // Vehicle registration lookup state: keyed by `${membershipIndex}_${memberIndex}`
  const [vehicleLookups, setVehicleLookups] = useState({});
  const [vehicleServerBlockedUntil, setVehicleServerBlockedUntil] = useState(() =>
    typeof window !== 'undefined' ? readVehicleServerBlockedUntil() : 0
  );
  const [vehicleSecurityUiRev, setVehicleSecurityUiRev] = useState(0);
  const bumpVehicleSecurityUi = () => setVehicleSecurityUiRev((x) => x + 1);

  useEffect(() => {
    const until = Math.max(vehicleServerBlockedUntil, readVehicleServerBlockedUntil());
    if (until <= Date.now()) {
      return undefined;
    }
    const id = setInterval(() => {
      if (Date.now() >= until) {
        setVehicleServerBlockedUntil(0);
        setVehicleServerBlockedUntilStorage(0);
      }
      bumpVehicleSecurityUi();
    }, 30000);
    return () => clearInterval(id);
  }, [vehicleServerBlockedUntil]);

  // Use ref to track current customer details without causing re-renders
  const customerDetailsRef = useRef(customerDetails);
  customerDetailsRef.current = customerDetails;

  // Track autofill activity and last seen non-empty values to stabilize bulk autofill
  const autofillActiveRef = useRef(false);
  const lastNonEmptyRef = useRef({});
  const pendingAutofillUpdateRef = useRef(null);

  // Initialize marketing consent and terms from context if available
  useEffect(() => {
    if (customerDetails?.contactable !== undefined) {
      setMarketingConsent(customerDetails.contactable);
    }
    if (customerDetails?.termsAccepted !== undefined) {
      setTermsAccepted(customerDetails.termsAccepted);
    }
  }, [customerDetails?.contactable, customerDetails?.termsAccepted]);

  useEffect(() => {
    if (isRegistered && customerDetails.email && !customerDetails.confirmEmail) {
      onUpdate({ ...customerDetails, confirmEmail: customerDetails.email });
    }
  }, [isRegistered, customerDetails, customerDetails?.email, customerDetails?.confirmEmail, onUpdate]);

  // Load form data for titles, countries, and settings
  useEffect(() => {
    const loadFormData = async () => {
      try {
        // Get titles from Venpos API
        const titlesResponse = await venposService.getTitles(locationId);
        if (!titlesResponse.isError && titlesResponse.data) {
          setTitles(titlesResponse.data);
        }

        // Get countries
        const countriesResponse = await venposService.getCountries(locationId);
        if (!countriesResponse.isError && countriesResponse.data) {
          // Sort countries to put United Kingdom (GB) first, then rest alphabetically
          const sortedCountries = [...countriesResponse.data].sort((a, b) => {
            if (a.lookupCountryID === 'GB') return -1;
            if (b.lookupCountryID === 'GB') return 1;
            return a.name.localeCompare(b.name);
          });
          setCountries(sortedCountries);
        }

        // Get registration settings
        const settingsResponse = await venposService.getCustomerRegistrationSettings(locationId);
        if (!settingsResponse.isError && settingsResponse.data) {
          setRegistrationSettings(settingsResponse.data);
        }
      } catch (error) {

      }
    };

    loadFormData();
  }, [locationId]);

  // Handle autofill detection and sync with React state
  useEffect(() => {
    let autofillWindowTimer = null;
    
    const markAutofillActive = () => {
      autofillActiveRef.current = true;
      if (autofillWindowTimer) clearTimeout(autofillWindowTimer);
      // Allow values to stabilize, then end the window and apply cached values
      autofillWindowTimer = setTimeout(() => {

        autofillActiveRef.current = false;
        
        // Build updates from cached values
        const cachedValues = lastNonEmptyRef.current;
        const updates = {};
        const currentDetails = customerDetailsRef.current;
        
        if (cachedValues.firstName && currentDetails.firstName !== cachedValues.firstName) {
          updates.firstName = cachedValues.firstName;
        }
        if (cachedValues.lastName && currentDetails.lastName !== cachedValues.lastName) {
          updates.lastName = cachedValues.lastName;
        }
        if (cachedValues.email && currentDetails.email !== cachedValues.email) {
          updates.email = cachedValues.email;
        }
        if (cachedValues.phone && currentDetails.phone !== cachedValues.phone) {
          // Format phone number from autofill
          updates.phone = formatPhoneNumber(cachedValues.phone);
        }
        
        // Handle address fields
        const addressUpdates = {};
        if (cachedValues.postcode && currentDetails.address?.postcode !== cachedValues.postcode) {
          addressUpdates.postcode = cachedValues.postcode;
        }
        if (cachedValues.houseNumber && currentDetails.address?.houseNumber !== cachedValues.houseNumber) {
          addressUpdates.houseNumber = cachedValues.houseNumber;
        }
        if (cachedValues.line1 && currentDetails.address?.line1 !== cachedValues.line1) {
          // Smart split if houseNumber is empty
          if (!currentDetails.address?.houseNumber && !addressUpdates.houseNumber) {
            const split = cachedValues.line1.trim().match(/^(\d+\w?)[\s,.-]+(.+)$/);
            if (split) {
              addressUpdates.houseNumber = split[1];
              addressUpdates.line1 = split[2];
            } else {
              addressUpdates.line1 = cachedValues.line1;
            }
          } else {
            addressUpdates.line1 = cachedValues.line1;
          }
        }
        if (cachedValues.line2 && currentDetails.address?.line2 !== cachedValues.line2) {
          addressUpdates.line2 = cachedValues.line2;
        }
        if (cachedValues.city && currentDetails.address?.city !== cachedValues.city) {
          addressUpdates.city = cachedValues.city;
        }
        if (cachedValues.country && currentDetails.address?.country !== cachedValues.country) {
          addressUpdates.country = cachedValues.country;
        }
        
        // Apply updates
        if (Object.keys(addressUpdates).length > 0) {
          const mergedAddress = { ...currentDetails.address, ...addressUpdates };
          onAddressUpdate(mergedAddress);

        }
        
        if (Object.keys(updates).length > 0) {
          const mergedDetails = { ...currentDetails, ...updates };
          onUpdate(mergedDetails);

        }
        
        // Clear cache and queue
        lastNonEmptyRef.current = {};
        pendingAutofillUpdateRef.current = null;
      }, 250); // Ultra-fast 250ms response
    };
    
    // Listen for autofill events
    const handleAutofillEvent = (e) => {
      // Remember the value immediately to prevent loss
      if (e.target.value && e.target.value.trim() !== '') {
        const fieldName = e.target.name || e.target.id;
        lastNonEmptyRef.current[fieldName] = e.target.value;
      }
      // Start/extend stabilization window
      markAutofillActive();
    };

    const inputs = document.querySelectorAll('input, select');
    inputs.forEach(input => {
      input.addEventListener('animationstart', handleAutofillEvent);
      input.addEventListener('change', handleAutofillEvent);
      input.addEventListener('input', handleAutofillEvent);
    });

    return () => {
      if (autofillWindowTimer) clearTimeout(autofillWindowTimer);
      inputs.forEach(input => {
        input.removeEventListener('animationstart', handleAutofillEvent);
        input.removeEventListener('change', handleAutofillEvent);
        input.removeEventListener('input', handleAutofillEvent);
      });
    };
  }, [onUpdate, onAddressUpdate]);

  const handleInputChange = useCallback((field, value) => {
    // Batch updates to prevent layout thrashing
    requestAnimationFrame(() => {
      // Format phone number as user types
      let formattedValue = value;
      if (field === 'phone') {
        // Strip out any non-numeric characters except +, spaces, and dashes
        const cleanedValue = value.replace(/[^\d+\s-]/g, '');
        formattedValue = formatPhoneNumber(cleanedValue);
      }
      
      const newDetails = { ...customerDetails, [field]: formattedValue };
      onUpdate(newDetails);
      
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: null }));
      }
      if (field === 'email' && errors.confirmEmail) {
        setErrors(prev => ({ ...prev, confirmEmail: null }));
      }
      
      // Clear validation message when user makes changes
      if (validationMessage) {
        setValidationMessage(null);
      }
    });
  }, [customerDetails, errors, validationMessage, onUpdate]);

  // Format UK postcode - adds space before last 3 characters
  const formatPostcode = (postcode) => {
    if (!postcode) return '';
    
    // Remove all spaces and convert to uppercase
    const cleaned = postcode.replace(/\s+/g, '').toUpperCase();
    
    // UK postcodes are typically 5-7 characters (without space)
    // Format: insert space before last 3 characters
    if (cleaned.length >= 5 && cleaned.length <= 7) {
      const outwardCode = cleaned.slice(0, -3);
      const inwardCode = cleaned.slice(-3);
      return `${outwardCode} ${inwardCode}`;
    }
    
    // Return as-is if too short or too long (let validation handle it)
    return cleaned;
  };

  const handleAddressChange = useCallback((field, value) => {
    // Batch updates to prevent layout thrashing
    requestAnimationFrame(() => {
      // Auto-format postcode
      let formattedValue = value;
      if (field === 'postcode') {
        formattedValue = formatPostcode(value);
      }
      // If user types full line1 and houseNumber empty, split automatically
      if (field === 'line1' && (!customerDetails.address?.houseNumber || customerDetails.address.houseNumber.trim() === '')) {
        const split = (formattedValue || '').trim().match(/^(\d+\w?)[\s,.-]+(.+)$/);
        if (split) {
          const newAddress = { ...customerDetails.address, houseNumber: split[1], line1: split[2] };
          onAddressUpdate(newAddress);
          if (errors['houseNumber']) {
            setErrors(prev => ({ ...prev, houseNumber: null }));
          }
          if (validationMessage) {
            setValidationMessage(null);
          }
          return;
        }
      }
      
      const newAddress = { ...customerDetails.address, [field]: formattedValue };
      onAddressUpdate(newAddress);
      
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: null }));
      }
      
      // Clear validation message when user makes changes
      if (validationMessage) {
        setValidationMessage(null);
      }
    });
  }, [customerDetails.address, errors, validationMessage, onAddressUpdate]);

  // Look up vehicle details via WordPress proxy (no secrets in the bundle).
  const lookupVehicleReg = useCallback(async (membershipIndex, memberIndex, rawVrm) => {
    const vrm = normaliseVRM(rawVrm);
    const key = `${membershipIndex}_${memberIndex}`;

    if (!vrm || vrm.length < 2) {
      setVehicleLookups(prev => ({ ...prev, [key]: undefined }));
      return;
    }

    const serverUntil = readVehicleServerBlockedUntil();
    if (serverUntil > Date.now()) {
      setVehicleServerBlockedUntil(serverUntil);
      const mins = Math.max(1, Math.ceil((serverUntil - Date.now()) / 60000));
      setVehicleLookups(prev => ({
        ...prev,
        [key]: {
          loading: false,
          vehicle: null,
          error: `Vehicle lookup is temporarily limited. Please try again in about ${mins} minute${mins === 1 ? '' : 's'}.`,
        },
      }));
      bumpVehicleSecurityUi();
      return;
    }

    if (isVehicleSessionBlocked()) {
      setVehicleLookups(prev => ({
        ...prev,
        [key]: {
          loading: false,
          vehicle: null,
          error: 'Vehicle lookup is paused in this browser. See the red notice above for what to do next.',
        },
      }));
      bumpVehicleSecurityUi();
      return;
    }

    setVehicleLookups(prev => ({ ...prev, [key]: { loading: true, vehicle: null, error: null } }));

    const finishError = (message) => {
      setVehicleLookups(prev => ({ ...prev, [key]: { loading: false, vehicle: null, error: message } }));
    };

    try {
      const vehicleLookupBase = config.vehicleLookupApiBase || config.apiEndpoint;
      const response = await fetch(`${vehicleLookupBase}/custom/v1/vehicle-lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vrm }),
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        bumpVehicleSecurityUi();
        const routeMissing =
          response.status === 404 ||
          data?.code === 'rest_no_route' ||
          (typeof data?.message === 'string' && data.message.toLowerCase().includes('no route'));
        if (routeMissing) {
          finishError(
            'Registration lookup is not available on the website yet (service not found). Please contact us for help.'
          );
          return;
        }
        finishError('Could not look up this registration. Please check your connection and try again.');
        return;
      }

      if (!data || typeof data !== 'object') {
        bumpVehicleSecurityUi();
        finishError('Registration lookup returned an unexpected response. Please try again later.');
        return;
      }

      if (data.blocked) {
        const secs = Number(data.retry_after_seconds) || 3600;
        const until = Date.now() + secs * 1000;
        setVehicleServerBlockedUntilStorage(until);
        setVehicleServerBlockedUntil(until);
        bumpVehicleSecurityUi();
        const mins = Math.max(1, Math.ceil(secs / 60));
        finishError(
          `Too many registration checks from your connection right now. Please try again in about ${mins} minute${mins === 1 ? '' : 's'}.`
        );
        return;
      }

      if (data.proxy_error === 'configuration') {
        bumpVehicleSecurityUi();
        finishError(
          'Registration lookup is not fully set up on the server yet. Please contact us so we can enable it, then try again.'
        );
        return;
      }

      if (data.proxy_error === 'upstream') {
        bumpVehicleSecurityUi();
        finishError(
          'The registration checking service is temporarily unavailable. Please try again in a few minutes.'
        );
        return;
      }

      if (data.proxy_error === 'invalid_vrm') {
        incrementVehicleLookupSessionFailCount();
        bumpVehicleSecurityUi();
        const blocked = isVehicleSessionBlocked();
        finishError(
          blocked
            ? 'Vehicle lookup is paused in this browser after several unsuccessful checks. See the red notice above for why and how to try again.'
            : 'That does not look like a valid registration. Check letters and numbers and try again.'
        );
        return;
      }

      if (data.valid && data.vehicle) {
        resetVehicleLookupSessionFailCount();
        bumpVehicleSecurityUi();
        setVehicleLookups(prev => ({ ...prev, [key]: { loading: false, vehicle: data.vehicle, error: null } }));
        setLocalMembershipDetails((prev) => {
          const next = prev.map((memb, mi) => {
            if (mi !== membershipIndex) return memb;
            const details = [...(memb.memberDetails || [])];
            const cur = { ...(details[memberIndex] || {}) };
            cur.vehicleRegistrationVerified = true;
            cur.vehicleRegistrationVerifiedVrm = vrm;
            details[memberIndex] = cur;
            return { ...memb, memberDetails: details };
          });
          if (onMemberDetailsUpdate) {
            onMemberDetailsUpdate(next);
          }
          return next;
        });
        return;
      }

      incrementVehicleLookupSessionFailCount();
      bumpVehicleSecurityUi();
      const blocked = isVehicleSessionBlocked();
      finishError(
        blocked
          ? 'Vehicle lookup is paused in this browser after several unsuccessful checks. See the red notice above for why and how to try again.'
          : 'Vehicle not found. Please double-check the registration.'
      );
    } catch {
      bumpVehicleSecurityUi();
      finishError('Could not look up this registration. Please check your connection and try again.');
    }
  }, [onMemberDetailsUpdate]);

  // Handle postcode address lookup
  const handleFindAddress = useCallback(async () => {
    const postcode = customerDetails.address?.postcode;
    
    if (!postcode || postcode.trim().length < 5) {
      setAddressLookup(prev => ({
        ...prev,
        error: 'Please enter a valid postcode'
      }));
      return;
    }
    
    setAddressLookup(prev => ({ ...prev, loading: true, error: null, addresses: [], selectedAddressId: '' }));
    
    try {
      const result = await venposService.getPostcodeAddresses(locationId, postcode);
      
      if (result.isError || !result.data || result.data.length === 0) {
        setAddressLookup(prev => ({
          ...prev,
          loading: false,
          error: 'No addresses found for this postcode. Please enter your address manually.',
          addresses: []
        }));
      } else {
        setAddressLookup(prev => ({
          ...prev,
          loading: false,
          addresses: result.data,
          error: null
        }));
      }
    } catch (error) {

      setAddressLookup(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to lookup addresses. Please enter your address manually.',
        addresses: []
      }));
    }
  }, [locationId, customerDetails.address?.postcode]);

  // Handle address selection from dropdown
  const handleAddressSelect = useCallback(async (addressId) => {
    if (!addressId) {
      setAddressLookup(prev => ({ ...prev, selectedAddressId: '' }));
      return;
    }
    
    setAddressLookup(prev => ({ ...prev, selectedAddressId: addressId, loading: true }));
    
    try {
      const result = await venposService.getAddressDetails(locationId, addressId);
      
      if (result.isError || !result.data) {
        setAddressLookup(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to fetch address details'
        }));
        return;
      }
      
      const addressData = result.data;

      // Auto-fill address fields with data from Venpos
      // Map Venpos field names to our form field names
      const newAddress = {
        houseNumber: addressData.addressNameNumber || addressData.houseNumber || addressData.buildingNumber || '',
        line1: addressData.addressLine1 || addressData.line1 || '',
        line2: addressData.addressLine2 || addressData.line2 || '',
        city: addressData.cityTown || addressData.city || addressData.town || '',
        postcode: addressData.postcode || addressData.postCode || customerDetails.address?.postcode || '',
        country: addressData.country || 'GB'
      };
      
      onAddressUpdate(newAddress);
      
      setAddressLookup(prev => ({ ...prev, loading: false }));

    } catch (error) {

      setAddressLookup(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to fetch address details'
      }));
    }
  }, [locationId, customerDetails.address?.postcode, onAddressUpdate]);

  // Debounced state update to prevent rapid layout changes
  const updateValidationStateStable = useCallback((loadingState, message) => {
    // Use requestAnimationFrame to batch state updates
    requestAnimationFrame(() => {
      setLoading(prev => ({ ...prev, validation: loadingState }));
      if (message !== undefined) {
        setValidationMessage(message);
      }
    });
  }, []);

  const validateWithVenpos = useCallback(async () => {
    if (!locationId || !selectedTickets?.length) return false;

    try {
      // Start loading with batched update
      updateValidationStateStable(true, null);

      // Create basket items from selected tickets for validation
      const basketItems = selectedTickets.map(ticket => ({
        pageType: 'WebPage',
        pageId: pageId,
        webSaleItemId: ticket.webSaleItemId,
        webSaleItemTypeID: ticket.webSaleItemTypeID || 2,
        quantity: ticket.quantity,
        selectedDate: selectedDate ? 
          formatDateLocal(new Date(selectedDate)) : null
      }));

      // Validate customer through basket flow (proper Venpos API integration)
      const sanitizedCustomerDetails = customerDetails ? { ...customerDetails } : {};
      if (sanitizedCustomerDetails.confirmEmail !== undefined) {
        delete sanitizedCustomerDetails.confirmEmail;
      }
      const response = await venposService.validateCustomerThroughBasket(
        locationId, 
        sanitizedCustomerDetails, 
        basketItems
      );
      
      if (response.isError) {
        updateValidationStateStable(false, {
          type: 'error',
          message: response.error || 'Validation failed. Please check your details and try again.'
        });
        return false;
      }

      // Validation successful through proper API flow
      updateValidationStateStable(false, {
        type: 'success',
        message: 'Customer details validated successfully.'
      });
      return true;

    } catch (error) {

      updateValidationStateStable(false, {
        type: 'error',
        message: 'Validation failed. Please try again.'
      });
      return false;
    }
  }, [locationId, selectedTickets, pageId, selectedDate, customerDetails, updateValidationStateStable]);

  // Handle member details updates
  const handleMemberDetailChange = (membershipIndex, memberIndex, field, value) => {
    const updated = [...localMembershipDetails];
    if (!updated[membershipIndex]) return;
    
    if (!updated[membershipIndex].memberDetails) {
      updated[membershipIndex].memberDetails = [{}];
    }
    if (!updated[membershipIndex].memberDetails[memberIndex]) {
      updated[membershipIndex].memberDetails[memberIndex] = {};
    }
    
    // Ensure value is a string
    const stringValue = value == null ? '' : String(value);
    
    // For mobile/phone fields, only allow numbers and phone formatting characters
    let formattedValue = stringValue;
    if (field === 'mobile' && stringValue) {
      // Strip out any non-numeric characters except +, spaces, and dashes
      const cleanedValue = stringValue.replace(/[^\d+\s-]/g, '');
      formattedValue = formatPhoneNumber(cleanedValue);
    }

    // For car registration (telephone field used as car reg), normalise: uppercase, strip spaces
    if (field === 'telephone' && stringValue) {
      formattedValue = normaliseVRM(stringValue);
    }
    
    updated[membershipIndex].memberDetails[memberIndex][field] = formattedValue;
    if (field === 'telephone') {
      updated[membershipIndex].memberDetails[memberIndex].vehicleRegistrationVerified = false;
      delete updated[membershipIndex].memberDetails[memberIndex].vehicleRegistrationVerifiedVrm;
      const lk = `${membershipIndex}_${memberIndex}`;
      setVehicleLookups((prev) => {
        const next = { ...prev };
        delete next[lk];
        return next;
      });
    }
    setLocalMembershipDetails(updated);
    
    // Notify parent component
    if (onMemberDetailsUpdate) {
      onMemberDetailsUpdate(updated);
    }
    
    // Validate the field in real-time and show immediate feedback
    const errorKey = `membership_${membershipIndex}_member_${memberIndex}_${field}`;
    const membership = updated[membershipIndex];
    
    // Only validate if the field has content (allow clearing without error)
    const trimmedValue = stringValue.trim();
    if (trimmedValue) {
      if (field === 'email' && (membership.emailRequired || membership.displayEmail)) {
        const emailValidation = validateRealEmail(trimmedValue);
        if (!emailValidation.valid) {
          setMemberErrors(prev => ({ ...prev, [errorKey]: emailValidation.error }));
          return;
        }
      }
      
      if (field === 'mobile' && (membership.mobilePhoneRequired || membership.displayMobilePhone)) {
        const phoneValidation = validateRealPhone(trimmedValue);
        if (!phoneValidation.valid) {
          setMemberErrors(prev => ({ ...prev, [errorKey]: phoneValidation.error }));
          return;
        }
      }
    }
    
    // Clear error for this field if validation passed or field is empty
    if (memberErrors[errorKey]) {
      setMemberErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  // Helper functions to identify membership types
  const validateMemberDetails = () => {
    const newMemberErrors = {};
    
    localMembershipDetails.forEach((membership, membershipIndex) => {
      if (membership.memberDetails) {
        membership.memberDetails.forEach((member, memberIndex) => {
          // First name validation
          if (!member.firstName?.trim()) {
            newMemberErrors[`membership_${membershipIndex}_member_${memberIndex}_firstName`] = 'First name is required';
          } else if (member.firstName.trim().length < 2) {
            newMemberErrors[`membership_${membershipIndex}_member_${memberIndex}_firstName`] = 'First name must be at least 2 characters';
          }
          
          // Last name validation
          if (!member.lastName?.trim()) {
            newMemberErrors[`membership_${membershipIndex}_member_${memberIndex}_lastName`] = 'Last name is required';
          } else if (member.lastName.trim().length < 2) {
            newMemberErrors[`membership_${membershipIndex}_member_${memberIndex}_lastName`] = 'Last name must be at least 2 characters';
          }
          
          // Email validation with stricter rules for memberships
          if (membership.emailRequired || (membership.displayEmail && member.email?.trim())) {
            if (!member.email?.trim()) {
              newMemberErrors[`membership_${membershipIndex}_member_${memberIndex}_email`] = 'Email is required';
            } else {
              const emailValidation = validateRealEmail(member.email.trim());
              if (!emailValidation.valid) {
                newMemberErrors[`membership_${membershipIndex}_member_${memberIndex}_email`] = emailValidation.error;
              }
            }
          }
          
          // Mobile phone validation with stricter rules for memberships
          if (membership.mobilePhoneRequired || (membership.displayMobilePhone && member.mobile?.trim())) {
            if (!member.mobile?.trim()) {
              newMemberErrors[`membership_${membershipIndex}_member_${memberIndex}_mobile`] = 'Mobile phone is required';
            } else {
              const phoneValidation = validateRealPhone(member.mobile.trim());
              if (!phoneValidation.valid) {
                newMemberErrors[`membership_${membershipIndex}_member_${memberIndex}_mobile`] = phoneValidation.error;
              }
            }
          }
          
          // Car registration: required, must pass DVLA-style lookup, and not while lookups are blocked
          if (shouldShowCarRegistration(membership, memberIndex, selectedTickets) && membership.displayTelephone) {
            const telKey = `membership_${membershipIndex}_member_${memberIndex}_telephone`;
            const serverUntil = Math.max(vehicleServerBlockedUntil, readVehicleServerBlockedUntil());
            const serverBlocked = serverUntil > Date.now();
            const sessionBlocked = isVehicleSessionBlocked();

            if (sessionBlocked) {
              newMemberErrors[telKey] =
                'Vehicle lookup is paused in this browser after several unsuccessful checks. See the red notice above for why and how to try again.';
            } else if (serverBlocked) {
              const mins = Math.max(1, Math.ceil((serverUntil - Date.now()) / 60000));
              newMemberErrors[telKey] = `Vehicle lookup is temporarily limited. Please try again in about ${mins} minute${mins === 1 ? '' : 's'}.`;
            } else if (!member.telephone?.trim()) {
              newMemberErrors[telKey] = 'Car registration is required';
            } else {
              const vrm = normaliseVRM(member.telephone);
              if (
                !member.vehicleRegistrationVerified ||
                member.vehicleRegistrationVerifiedVrm !== vrm
              ) {
                newMemberErrors[telKey] =
                  'Your registration must be verified. Enter it, then click or tab away from the field so we can look up your vehicle and confirm the details match.';
              }
            }
          }
        });
      }
    });
    
    setMemberErrors(newMemberErrors);
    return Object.keys(newMemberErrors).length === 0;
  };

  const validateForm = () => {
    const customerErrors = validateCustomerDetails(customerDetails);
    const addressErrors = validateAddress(customerDetails.address);
    const allErrors = { ...customerErrors, ...addressErrors };
    
    // Always require terms acceptance
    if (!termsAccepted) {
      allErrors.terms = 'You must accept the terms and conditions';
    }

    if (!registrationSettings.titleIsOptional && (!customerDetails.selectedTitle || customerDetails.selectedTitle === 0)) {
      allErrors.selectedTitle = 'Please select a title';
    }
    
    setErrors(allErrors);
    
    // Validate member details ONLY if this is a membership journey
    let memberDetailsValid = true;
    if (isMembershipJourney && localMembershipDetails && localMembershipDetails.length > 0) {
      memberDetailsValid = validateMemberDetails();
    }
    
    return Object.keys(allErrors).length === 0 && memberDetailsValid;
  };

  // Debounced validation to prevent rapid API calls
  const debouncedValidation = useMemo(() => {
    let timeoutId;
    return async (field) => {
      if (timeoutId) clearTimeout(timeoutId);
      
      timeoutId = setTimeout(async () => {
        // Only validate when all essential fields are completed
        const email = customerDetails.email?.trim();
        const confirmEmail = customerDetails.confirmEmail?.trim();
        const emailsMatch = email && confirmEmail && email.toLowerCase() === confirmEmail.toLowerCase();
        const hasRequiredFields = emailsMatch && 
                                 customerDetails.firstName && 
                                 customerDetails.lastName && 
                                 customerDetails.phone &&
                                 customerDetails.address?.postcode;
        
        if ((field === 'email' || field === 'confirmEmail') && hasRequiredFields) {

          await validateWithVenpos();
        }
      }, 1000); // Increased delay to prevent premature validation
    };
  }, [customerDetails.email, customerDetails.confirmEmail, customerDetails.firstName, customerDetails.lastName, customerDetails.phone, customerDetails.address?.postcode, validateWithVenpos]);

  // Validate on blur
  const handleBlur = useCallback(async (field) => {
    // Check if the field element is autofilled
    const fieldElement = document.getElementById(field);
    const isAutofilled = fieldElement && fieldElement.matches(':-webkit-autofill, :autofill');
    
    // Don't validate if field is autofilled (browser will handle it)
    if (isAutofilled) {

      return;
    }
    
    const customerErrors = validateCustomerDetails(customerDetails);
    const addressErrors = validateAddress(customerDetails.address);
    const allErrors = { ...customerErrors, ...addressErrors };
    
    // Batch error updates
    requestAnimationFrame(() => {
      if (allErrors[field]) {
        setErrors(prev => ({ ...prev, [field]: allErrors[field] }));
      }
    });

    // Only trigger Venpos validation on email blur (and only if all fields are filled)
    if (field === 'email' || field === 'confirmEmail') {
      await debouncedValidation(field);
    }
  }, [customerDetails, debouncedValidation]);

  // Check what required fields are missing
  const getMissingFields = () => {
    const missing = [];
    const email = customerDetails.email?.trim();
    const confirmEmail = customerDetails.confirmEmail?.trim();
    
    if (!customerDetails.firstName || customerDetails.firstName.trim() === '') {
      missing.push('First Name');
    }
    if (!customerDetails.lastName || customerDetails.lastName.trim() === '') {
      missing.push('Last Name');
    }
    if (!email) {
      missing.push('Email Address');
    }
    if (!isRegistered) {
      if (!confirmEmail) {
        missing.push('Confirm Email Address');
      } else if (email && email.toLowerCase() !== confirmEmail.toLowerCase()) {
        missing.push('Email addresses must match');
      }
    }
    if (!customerDetails.phone || customerDetails.phone.trim() === '') {
      missing.push('Phone Number');
    }
    if (!customerDetails.address?.houseNumber || customerDetails.address.houseNumber.trim() === '') {
      missing.push('House/Building Name or Number');
    }
    if (!customerDetails.address?.line1 || customerDetails.address.line1.trim() === '') {
      missing.push('Address Line 1');
    }
    if (!customerDetails.address?.city || customerDetails.address.city.trim() === '') {
      missing.push('City');
    }
    if (!customerDetails.address?.postcode || customerDetails.address.postcode.trim() === '') {
      missing.push('Postcode');
    }
    if (!customerDetails.address?.country || customerDetails.address.country.trim() === '') {
      missing.push('Country');
    }
    if (!termsAccepted) {
      missing.push('Accept Terms and Conditions');
    }
    
    // Check membership details if this is a membership journey
    if (isMembershipJourney && localMembershipDetails && localMembershipDetails.length > 0) {
      localMembershipDetails.forEach((membership, membershipIndex) => {
        if (membership.memberDetails) {
          membership.memberDetails.forEach((member, memberIndex) => {
            if (!member.firstName?.trim()) {
              missing.push(`Membership ${membershipIndex + 1}, Member ${memberIndex + 1}: First Name`);
            }
            if (!member.lastName?.trim()) {
              missing.push(`Membership ${membershipIndex + 1}, Member ${memberIndex + 1}: Last Name`);
            }
            if (membership.emailRequired && !member.email?.trim()) {
              missing.push(`Membership ${membershipIndex + 1}, Member ${memberIndex + 1}: Email`);
            }
            if (membership.mobilePhoneRequired && !member.mobile?.trim()) {
              missing.push(`Membership ${membershipIndex + 1}, Member ${memberIndex + 1}: Mobile Phone`);
            }
            if (shouldShowCarRegistration(membership, memberIndex, selectedTickets) && membership.displayTelephone) {
              if (!member.telephone?.trim()) {
                missing.push(`Membership ${membershipIndex + 1}, Member ${memberIndex + 1}: Car Registration`);
              } else {
                const vrm = normaliseVRM(member.telephone);
                if (!member.vehicleRegistrationVerified || member.vehicleRegistrationVerifiedVrm !== vrm) {
                  missing.push(`Membership ${membershipIndex + 1}, Member ${memberIndex + 1}: Verified Car Registration`);
                }
              }
            }
          });
        } else {
          missing.push(`Membership ${membershipIndex + 1}: Member details required`);
        }
      });
    }
    
    return missing;
  };

  const missingFields = getMissingFields();
  const isFormComplete = missingFields.length === 0;

  const effectiveVehicleServerBlockUntil = Math.max(vehicleServerBlockedUntil, readVehicleServerBlockedUntil());
  const vehicleLookupServerBlocked = effectiveVehicleServerBlockUntil > Date.now();
  const vehicleCarRegInputDisabled = isVehicleSessionBlocked() || vehicleLookupServerBlocked;

  const handleFormSubmit = (e) => {
    e.preventDefault();
    // Form submission is handled by the parent component's navigation
  };

  return (
    <CustomerContainer>
      {/* Belvoir account sign-in / sign-up – only when not already registered */}
      {!isRegistered && (
        <AccountSectionComingSoonWrap>
          <AccountSection aria-hidden="true">
            <AccountSectionTitle>Belvoir account</AccountSectionTitle>
            <AccountRow>
              <span style={{ fontSize: '0.95rem', color: 'var(--color-secondary, #666)' }}>
                Already have a Belvoir account?
              </span>
              <SignInButton type="button" tabIndex={-1} disabled>
                Sign in
              </SignInButton>
            </AccountRow>
            <AccountDivider />
            <CreateAccountCheckboxWrap>
              <Checkbox
                id="create-belvoir-account"
                type="checkbox"
                checked={createAccountChecked}
                onChange={(e) => setCreateAccountChecked(e.target.checked)}
                disabled
                tabIndex={-1}
              />
              <CheckboxLabel htmlFor="create-belvoir-account" style={{ cursor: 'default', marginBottom: 0 }}>
                Create a free Belvoir account to manage this booking in the app and access member benefits.
              </CheckboxLabel>
            </CreateAccountCheckboxWrap>
            <GuestHint>Or continue as guest below — we'll still need your details for the booking.</GuestHint>
          </AccountSection>
          <AccountSectionComingSoonOverlay>
            <AccountSectionComingSoonText>Coming soon</AccountSectionComingSoonText>
          </AccountSectionComingSoonOverlay>
        </AccountSectionComingSoonWrap>
      )}

      <PageHeader>
        <PageHeaderAccent aria-hidden />
        <SectionEyebrow>Almost there</SectionEyebrow>
        <SectionTitle>Your Contact Details</SectionTitle>
        <SectionSubtitle>
          We use these details for your booking confirmation and any important updates about your visit.
        </SectionSubtitle>
      </PageHeader>

      {isRegistered && (
        <RegistrationBanner>
          <RegistrationInfo>
            <RegistrationTitle>
              Customer details saved
            </RegistrationTitle>
            <RegistrationText>
              Your details have been registered with our system. If you need to change your information, please click "Change Details" to restart the registration process.
            </RegistrationText>
          </RegistrationInfo>
          {onClearRegistration && (
            <ChangeDetailsButton onClick={onClearRegistration}>
              Change details
            </ChangeDetailsButton>
          )}
        </RegistrationBanner>
      )}

      <form onSubmit={handleFormSubmit} autoComplete="on">
      <FormSection style={{ opacity: isRegistered ? 0.6 : 1 }}>
        <SectionHeader>
          Personal Information {isRegistered && <span style={{ fontSize: '0.9rem', color: '#17a2b8', fontWeight: 'normal' }}>(Locked)</span>}
        </SectionHeader>
        
        <FormGrid>
          <FormGroup>
            <Label htmlFor="title">
              Title {!registrationSettings?.titleIsOptional && <Required>*</Required>}
            </Label>
            <Select
              id="title"
              name="honorific-prefix"
              autoComplete="honorific-prefix"
              value={customerDetails.selectedTitle || 0}
              onChange={(e) => handleInputChange('selectedTitle', parseInt(e.target.value))}
              $hasError={errors.selectedTitle}
              disabled={isRegistered}
            >
              <option value={0}>Select Title</option>
              {titles.length > 0 && titles.map((titleItem) => (
                <option key={titleItem.lookupTitleID} value={titleItem.lookupTitleID}>
                  {titleItem.title}
                </option>
              ))}
            </Select>
            {errors.selectedTitle && <ErrorMessage>{errors.selectedTitle}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="firstName">
              First Name <Required>*</Required>
            </Label>
            <Input
              id="firstName"
              name="firstName"
              type="text"
              autoComplete="given-name"
              autoCapitalize="words"
              value={customerDetails.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              onBlur={() => handleBlur('firstName')}
              placeholder="Enter your first name"
              $hasError={errors.firstName}
              disabled={isRegistered}
            />
            {errors.firstName && <ErrorMessage>{errors.firstName}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="lastName">
              Last Name <Required>*</Required>
            </Label>
            <Input
              id="lastName"
              name="lastName"
              type="text"
              autoComplete="family-name"
              autoCapitalize="words"
              value={customerDetails.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              onBlur={() => handleBlur('lastName')}
              placeholder="Enter your last name"
              $hasError={errors.lastName}
              disabled={isRegistered}
            />
            {errors.lastName && <ErrorMessage>{errors.lastName}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="email">
              Email Address <Required>*</Required>
              <span style={{ width: '20px', height: '20px', display: 'inline-block' }}>
                {loading.validation && <LoadingSpinner />}
              </span>
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              value={customerDetails.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              onBlur={() => handleBlur('email')}
              placeholder="Enter your email address"
              $hasError={errors.email}
              disabled={isRegistered}
            />
            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="confirmEmail">
              Confirm Email Address <Required>*</Required>
            </Label>
            <Input
              id="confirmEmail"
              name="confirmEmail"
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              value={customerDetails.confirmEmail || ''}
              onChange={(e) => handleInputChange('confirmEmail', e.target.value)}
              onBlur={() => handleBlur('confirmEmail')}
              placeholder="Re-enter your email address"
              $hasError={errors.confirmEmail}
              disabled={isRegistered}
            />
            {errors.confirmEmail && <ErrorMessage>{errors.confirmEmail}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="phone">
              Phone Number <Required>*</Required>
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              autoComplete="tel"
              autoCapitalize="none"
              autoCorrect="off"
              inputMode="tel"
              value={customerDetails.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              onBlur={() => handleBlur('phone')}
              placeholder="e.g., 07123 456789 or +44 7123 456789"
              $hasError={errors.phone}
              disabled={isRegistered}
            />
            {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
            {!errors.phone && customerDetails.phone && (
              <FieldHint>UK mobile or landline numbers accepted</FieldHint>
            )}
          </FormGroup>
        </FormGrid>

        <div style={{ minHeight: '60px', transition: 'all 0.3s ease' }}>
          {validationMessage && (
            <ValidationMessage $isSuccess={validationMessage.type === 'success'}>
              {validationMessage.message}
            </ValidationMessage>
          )}
        </div>
      </FormSection>

      <FormSection style={{ opacity: isRegistered ? 0.6 : 1 }}>
        <SectionHeader>
          Address Information {isRegistered && <span style={{ fontSize: '0.9rem', color: '#17a2b8', fontWeight: 'normal' }}>(Locked)</span>}
        </SectionHeader>
        
        <PostcodeLookupSection>
          <PostcodeHeader>
            <PostcodeTitle>Find Your Address</PostcodeTitle>
            <PostcodeHint>
              Enter your postcode to quickly find and auto-fill your address
            </PostcodeHint>
          </PostcodeHeader>
          
          <Label htmlFor="postcode">
            Postcode <Required>*</Required>
          </Label>
          <PostcodeInputGroup>
            <PostcodeInput
              id="postcode"
              name="postcode"
              type="text"
              autoComplete="postal-code"
              autoCapitalize="characters"
              autoCorrect="off"
              value={customerDetails.address?.postcode || ''}
              onChange={(e) => handleAddressChange('postcode', e.target.value)}
              onBlur={() => handleBlur('postcode')}
              placeholder="Postcode"
              $hasError={errors.postcode}
              disabled={isRegistered}
            />
            <FindAddressButton
              type="button"
              onClick={handleFindAddress}
              disabled={isRegistered || addressLookup.loading || !customerDetails.address?.postcode}
            >
              {addressLookup.loading ? (
                <>
                  <LoadingSpinner /> Finding...
                </>
              ) : (
                'Find Address'
              )}
            </FindAddressButton>
          </PostcodeInputGroup>
          {errors.postcode && <ErrorMessage>{errors.postcode}</ErrorMessage>}
          
          {addressLookup.addresses.length > 0 && (
            <AddressDropdownContainer>
              <Label htmlFor="addressSelect">Select Your Address</Label>
              <AddressSelect
                id="addressSelect"
                value={addressLookup.selectedAddressId}
                onChange={(e) => handleAddressSelect(e.target.value)}
                disabled={isRegistered}
              >
                <option value="">-- Select an address --</option>
                {addressLookup.addresses.map((addr, index) => (
                  <option key={addr.id || index} value={addr.id}>
                    {addr.addressText || addr.summaryline || addr.address || `Address ${index + 1}`}
                  </option>
                ))}
              </AddressSelect>
              <AddressHint>
                Select an address from the list to auto-fill the form below
              </AddressHint>
            </AddressDropdownContainer>
          )}
          
          {addressLookup.error && (
            <ErrorMessage>{addressLookup.error}</ErrorMessage>
          )}
          
          <ManualEntryHint>
            Or enter your address details manually below
          </ManualEntryHint>
        </PostcodeLookupSection>
        
        <FormGrid>

          <FormGroup>
            <Label htmlFor="houseNumber">
              House/Building Name or Number <Required>*</Required>
            </Label>
            <Input
              id="houseNumber"
              name="houseNumber"
              type="text"
              autoComplete="on"
              autoCapitalize="words"
              value={customerDetails.address?.houseNumber || ''}
              onChange={(e) => handleAddressChange('houseNumber', e.target.value)}
              onBlur={() => handleBlur('houseNumber')}
              placeholder="House/Building Name or Number"
              $hasError={errors.houseNumber}
              disabled={isRegistered}
            />
            {errors.houseNumber && <ErrorMessage>{errors.houseNumber}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="line1">
              Address Line 1 <Required>*</Required>
            </Label>
            <Input
              id="line1"
              name="line1"
              type="text"
              autoComplete="address-line1"
              autoCapitalize="words"
              value={customerDetails.address?.line1 || ''}
              onChange={(e) => handleAddressChange('line1', e.target.value)}
              onBlur={() => handleBlur('line1')}
              placeholder="Street name"
              $hasError={errors.line1}
              disabled={isRegistered}
            />
            {errors.line1 && <ErrorMessage>{errors.line1}</ErrorMessage>}
          </FormGroup>

          <FullWidthGroup>
            <Label htmlFor="line2">
              Address Line 2
            </Label>
            <Input
              id="line2"
              name="line2"
              type="text"
              autoComplete="address-line2"
              autoCapitalize="words"
              value={customerDetails.address?.line2 || ''}
              onChange={(e) => handleAddressChange('line2', e.target.value)}
              placeholder="Apartment, suite, etc. (optional)"
              disabled={isRegistered}
            />
          </FullWidthGroup>

          <FormGroup>
            <Label htmlFor="city">
              City <Required>*</Required>
            </Label>
            <Input
              id="city"
              name="city"
              type="text"
              autoComplete="address-level2"
              autoCapitalize="words"
              value={customerDetails.address?.city || ''}
              onChange={(e) => handleAddressChange('city', e.target.value)}
              onBlur={() => handleBlur('city')}
              placeholder="Enter your city"
              $hasError={errors.city}
            />
            {errors.city && <ErrorMessage>{errors.city}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="country">
              Country <Required>*</Required>
            </Label>
            <Select
              id="country"
              name="country"
              autoComplete="country-name"
              value={customerDetails.address?.country || 'GB'}
              onChange={(e) => handleAddressChange('country', e.target.value)}
              $hasError={errors.country}
              disabled={isRegistered}
            >
              {countries.length > 0 ? (
                countries.map((country) => (
                  <option key={country.lookupCountryID} value={country.lookupCountryID}>
                    {country.name}
                  </option>
                ))
              ) : (
                <option value="GB">United Kingdom</option>
              )}
            </Select>
            {errors.country && <ErrorMessage>{errors.country}</ErrorMessage>}
          </FormGroup>
        </FormGrid>
      </FormSection>

      {/* Member Details Section for Membership Journeys ONLY */}
      {isMembershipJourney && localMembershipDetails && localMembershipDetails.length > 0 && (
        <>
          {(isVehicleSessionBlocked() || vehicleLookupServerBlocked) && (
            <FormSection key={`vehicle-block-banner-${vehicleSecurityUiRev}`}>
              <VehicleSecurityBanner>
                {isVehicleSessionBlocked() && (
                  <>
                    <VehicleSecurityBannerTitle>Vehicle lookup blocked in this browser</VehicleSecurityBannerTitle>
                    <p>
                      Several registration lookups did not return a matching vehicle, so further checks are paused here to protect our systems from abuse.
                    </p>
                    <p>
                      <strong>When you can try again:</strong> open this page in a new private or incognito window, or start a fresh visit after closing every tab for this website. If you still cannot continue, please contact us for help.
                    </p>
                  </>
                )}
                {vehicleLookupServerBlocked && (
                  <>
                    <VehicleSecurityBannerTitle>Vehicle lookup temporarily limited</VehicleSecurityBannerTitle>
                    <p>
                      Too many registration checks are being made from your network. This usually clears automatically after a short cooling-off period.
                    </p>
                    <p>
                      <strong>When to try again:</strong> in approximately{' '}
                      {Math.max(1, Math.ceil((effectiveVehicleServerBlockUntil - Date.now()) / 60000))} minute
                      {Math.ceil((effectiveVehicleServerBlockUntil - Date.now()) / 60000) === 1 ? '' : 's'}.
                    </p>
                  </>
                )}
              </VehicleSecurityBanner>
            </FormSection>
          )}
          {localMembershipDetails.map((membership, membershipIndex) => {
            const memberDetails = (membership.memberDetails || []).map(md => ({
              useBillingAddress: md.useBillingAddress !== false, // default true
              ...md,
            }));
            const memberCount = membership.memberCount || 1;
            
            // Ensure we have enough member detail objects
            while (memberDetails.length < memberCount) {
              memberDetails.push({ useBillingAddress: true });
            }
            
            return (
              <FormSection key={`membership-${membershipIndex}`}>
                <SectionHeader>
                  {membership.description || membership.alternativeDescription || `Membership ${membershipIndex + 1}`}
                  {memberCount > 1 && <span style={{ fontSize: '0.9rem', color: '#666', fontWeight: 'normal' }}> ({memberCount} {memberCount === 1 ? 'member' : 'members'})</span>}
                </SectionHeader>
                
                {memberDetails.map((member, memberIndex) => {
                  const useBillingAddress = member.useBillingAddress !== false; // default to true
                  
                  return (
                  <MemberDetailsCard key={`member-${membershipIndex}-${memberIndex}`}>
                    {memberCount > 1 && (
                      <MemberTitle>
                        Member {memberIndex + 1}
                      </MemberTitle>
                    )}
                    
                    <FormGrid>
                      <FormGroup>
                        <Label htmlFor={`member-${membershipIndex}-${memberIndex}-title`}>
                          Title
                        </Label>
                        <Select
                          id={`member-${membershipIndex}-${memberIndex}-title`}
                          value={member.selectedTitle || 0}
                          onChange={(e) => handleMemberDetailChange(membershipIndex, memberIndex, 'selectedTitle', parseInt(e.target.value))}
                          $hasError={memberErrors[`membership_${membershipIndex}_member_${memberIndex}_selectedTitle`]}
                        >
                          <option value={0}>Select Title</option>
                          {titles.length > 0 && titles.map((titleItem) => (
                            <option key={titleItem.lookupTitleID} value={titleItem.lookupTitleID}>
                              {titleItem.title}
                            </option>
                          ))}
                        </Select>
                        {memberErrors[`membership_${membershipIndex}_member_${memberIndex}_selectedTitle`] && (
                          <ErrorMessage>{memberErrors[`membership_${membershipIndex}_member_${memberIndex}_selectedTitle`]}</ErrorMessage>
                        )}
                      </FormGroup>

                      <FormGroup>
                        <Label htmlFor={`member-${membershipIndex}-${memberIndex}-firstName`}>
                          First Name <Required>*</Required>
                        </Label>
                        <Input
                          id={`member-${membershipIndex}-${memberIndex}-firstName`}
                          type="text"
                          autoComplete="given-name"
                          autoCapitalize="words"
                          value={member.firstName || ''}
                          onChange={(e) => handleMemberDetailChange(membershipIndex, memberIndex, 'firstName', e.target.value)}
                          placeholder="Enter first name"
                          $hasError={memberErrors[`membership_${membershipIndex}_member_${memberIndex}_firstName`]}
                        />
                        {memberErrors[`membership_${membershipIndex}_member_${memberIndex}_firstName`] && (
                          <ErrorMessage>{memberErrors[`membership_${membershipIndex}_member_${memberIndex}_firstName`]}</ErrorMessage>
                        )}
                      </FormGroup>

                      <FormGroup>
                        <Label htmlFor={`member-${membershipIndex}-${memberIndex}-lastName`}>
                          Last Name <Required>*</Required>
                        </Label>
                        <Input
                          id={`member-${membershipIndex}-${memberIndex}-lastName`}
                          type="text"
                          autoComplete="family-name"
                          autoCapitalize="words"
                          value={member.lastName || ''}
                          onChange={(e) => handleMemberDetailChange(membershipIndex, memberIndex, 'lastName', e.target.value)}
                          placeholder="Enter last name"
                          $hasError={memberErrors[`membership_${membershipIndex}_member_${memberIndex}_lastName`]}
                        />
                        {memberErrors[`membership_${membershipIndex}_member_${memberIndex}_lastName`] && (
                          <ErrorMessage>{memberErrors[`membership_${membershipIndex}_member_${memberIndex}_lastName`]}</ErrorMessage>
                        )}
                      </FormGroup>

                      {membership.displayEmail && (
                        <FormGroup>
                          <Label htmlFor={`member-${membershipIndex}-${memberIndex}-email`}>
                            Email {membership.emailRequired && <Required>*</Required>}
                          </Label>
                          <Input
                            id={`member-${membershipIndex}-${memberIndex}-email`}
                            type="email"
                            autoComplete="email"
                            autoCapitalize="none"
                            autoCorrect="off"
                            value={member.email || ''}
                            onChange={(e) => handleMemberDetailChange(membershipIndex, memberIndex, 'email', e.target.value)}
                            placeholder="e.g., name@example.com"
                            $hasError={memberErrors[`membership_${membershipIndex}_member_${memberIndex}_email`]}
                          />
                          {memberErrors[`membership_${membershipIndex}_member_${memberIndex}_email`] && (
                            <ErrorMessage>{memberErrors[`membership_${membershipIndex}_member_${memberIndex}_email`]}</ErrorMessage>
                          )}
                        </FormGroup>
                      )}

                      {membership.displayMobilePhone && (
                        <FormGroup>
                          <Label htmlFor={`member-${membershipIndex}-${memberIndex}-mobile`}>
                            Mobile Phone {membership.mobilePhoneRequired && <Required>*</Required>}
                          </Label>
                          <Input
                            id={`member-${membershipIndex}-${memberIndex}-mobile`}
                            type="tel"
                            autoComplete="tel"
                            inputMode="tel"
                            value={member.mobile || ''}
                            onChange={(e) => handleMemberDetailChange(membershipIndex, memberIndex, 'mobile', e.target.value)}
                            placeholder="e.g., 07123 456789"
                            $hasError={memberErrors[`membership_${membershipIndex}_member_${memberIndex}_mobile`]}
                          />
                          {memberErrors[`membership_${membershipIndex}_member_${memberIndex}_mobile`] && (
                            <ErrorMessage>{memberErrors[`membership_${membershipIndex}_member_${memberIndex}_mobile`]}</ErrorMessage>
                          )}
                        </FormGroup>
                      )}

                      {shouldShowCarRegistration(membership, memberIndex, selectedTickets) && membership.displayTelephone && (
                        <FormGroup>
                          <Label htmlFor={`member-${membershipIndex}-${memberIndex}-telephone`}>
                            Car Registration number <Required>*</Required>
                          </Label>
                          <Input
                            id={`member-${membershipIndex}-${memberIndex}-telephone`}
                            type="text"
                            autoComplete="off"
                            value={member.telephone || ''}
                            onChange={(e) => handleMemberDetailChange(membershipIndex, memberIndex, 'telephone', e.target.value)}
                            onBlur={(e) => {
                              const vrm = normaliseVRM(e.target.value);
                              if (vrm.length >= 2) {
                                lookupVehicleReg(membershipIndex, memberIndex, vrm);
                              }
                            }}
                            placeholder="e.g. AB12 CDE"
                            disabled={vehicleCarRegInputDisabled}
                            $hasError={memberErrors[`membership_${membershipIndex}_member_${memberIndex}_telephone`]}
                          />
                          {memberErrors[`membership_${membershipIndex}_member_${memberIndex}_telephone`] && (
                            <ErrorMessage>{memberErrors[`membership_${membershipIndex}_member_${memberIndex}_telephone`]}</ErrorMessage>
                          )}
                          {(() => {
                            const lookup = vehicleLookups[`${membershipIndex}_${memberIndex}`];
                            if (!lookup) return null;
                            if (lookup.loading) return <VehicleLookupLoading>Looking up vehicle...</VehicleLookupLoading>;
                            if (lookup.error) return <VehicleLookupError>{lookup.error}</VehicleLookupError>;
                            if (lookup.vehicle) {
                              const v = lookup.vehicle;
                              return (
                                <VehicleCard>
                                  <VehicleRegPlate>{v.registrationNumber}</VehicleRegPlate>
                                  <VehicleCardTitle>{v.make} {v.model}</VehicleCardTitle>
                                  <VehicleGrid>
                                    {v.colour && <VehicleField><VehicleFieldLabel>Colour:</VehicleFieldLabel> {v.colour}</VehicleField>}
                                    {v.fuelType && <VehicleField><VehicleFieldLabel>Fuel:</VehicleFieldLabel> {v.fuelType}</VehicleField>}
                                    {v.yearOfManufacture && <VehicleField><VehicleFieldLabel>Year:</VehicleFieldLabel> {v.yearOfManufacture}</VehicleField>}
                                    {v.engineCapacity && <VehicleField><VehicleFieldLabel>Engine:</VehicleFieldLabel> {v.engineCapacity}cc</VehicleField>}
                                  </VehicleGrid>
                                </VehicleCard>
                              );
                            }
                            return null;
                          })()}
                        </FormGroup>
                      )}

                      <>
                        <FormGroup style={{ gridColumn: '1 / -1' }}>
                          <CheckboxContainer>
                            <Checkbox
                              id={`member-${membershipIndex}-${memberIndex}-useBillingAddress`}
                              type="checkbox"
                              checked={useBillingAddress}
                              onChange={(e) => handleMemberDetailChange(membershipIndex, memberIndex, 'useBillingAddress', e.target.checked)}
                            />
                            <CheckboxLabel htmlFor={`member-${membershipIndex}-${memberIndex}-useBillingAddress`}>
                              Use billing address
                            </CheckboxLabel>
                          </CheckboxContainer>
                        </FormGroup>

                        {!useBillingAddress && (
                          <>
                            <FormGroup style={{ gridColumn: '1 / -1' }}>
                              <Label htmlFor={`member-${membershipIndex}-${memberIndex}-addressFirstLine`}>
                                Address Line 1
                              </Label>
                              <Input
                                id={`member-${membershipIndex}-${memberIndex}-addressFirstLine`}
                                type="text"
                                autoComplete="address-line1"
                                value={member.addressFirstLine || ''}
                                onChange={(e) => handleMemberDetailChange(membershipIndex, memberIndex, 'addressFirstLine', e.target.value)}
                                placeholder="Enter address line 1"
                              />
                            </FormGroup>

                            <FormGroup style={{ gridColumn: '1 / -1' }}>
                              <Label htmlFor={`member-${membershipIndex}-${memberIndex}-addressSecondLine`}>
                                Address Line 2
                              </Label>
                              <Input
                                id={`member-${membershipIndex}-${memberIndex}-addressSecondLine`}
                                type="text"
                                autoComplete="address-line2"
                                value={member.addressSecondLine || ''}
                                onChange={(e) => handleMemberDetailChange(membershipIndex, memberIndex, 'addressSecondLine', e.target.value)}
                                placeholder="Enter address line 2 (optional)"
                              />
                            </FormGroup>

                            <FormGroup>
                              <Label htmlFor={`member-${membershipIndex}-${memberIndex}-cityTown`}>
                                City/Town
                              </Label>
                              <Input
                                id={`member-${membershipIndex}-${memberIndex}-cityTown`}
                                type="text"
                                autoComplete="address-level2"
                                value={member.cityTown || ''}
                                onChange={(e) => handleMemberDetailChange(membershipIndex, memberIndex, 'cityTown', e.target.value)}
                                placeholder="Enter city or town"
                              />
                            </FormGroup>

                            <FormGroup>
                              <Label htmlFor={`member-${membershipIndex}-${memberIndex}-postCode`}>
                                Postcode
                              </Label>
                              <Input
                                id={`member-${membershipIndex}-${memberIndex}-postCode`}
                                type="text"
                                autoComplete="postal-code"
                                value={member.postCode || ''}
                                onChange={(e) => handleMemberDetailChange(membershipIndex, memberIndex, 'postCode', e.target.value)}
                                placeholder="Enter postcode"
                              />
                            </FormGroup>

                            <FormGroup>
                              <Label htmlFor={`member-${membershipIndex}-${memberIndex}-selectedCountry`}>
                                Country
                              </Label>
                              <Select
                                id={`member-${membershipIndex}-${memberIndex}-selectedCountry`}
                                value={member.selectedCountry || 'GB'}
                                onChange={(e) => handleMemberDetailChange(membershipIndex, memberIndex, 'selectedCountry', e.target.value)}
                              >
                                {countries.map((country) => (
                                  <option key={country.code} value={country.code}>
                                    {country.name}
                                  </option>
                                ))}
                                {countries.length === 0 && (
                                  <option value="GB">United Kingdom</option>
                                )}
                              </Select>
                            </FormGroup>
                          </>
                        )}
                      </>
                    </FormGrid>
                  </MemberDetailsCard>
                );
                })}
              </FormSection>
            );
          })}
        </>
      )}

      {/* Questions Section for Grotto Retail Village */}
      {isGrottoRetailVillage && grottoQuestions && grottoQuestions.length > 0 && (
        <FormSection>
          <SectionHeader>
            Additional Information
          </SectionHeader>
          <p style={{ marginBottom: '1.5rem', color: '#666', fontSize: '0.95rem' }}>
            Please answer the following questions for your booking
          </p>
          {(() => {
            const occurrenceMap = {};
            return grottoQuestions.map((question, index) => {
              const occ = occurrenceMap[question.questionId] || 0;
              const qKey = makeQuestionKey(question.questionId, occ);
              occurrenceMap[question.questionId] = occ + 1;
              return (
                <FormGroup key={`${question.questionId}_${occ}`} style={{ marginBottom: '1.5rem' }}>
                  <Label>
                    {question.question}
                    {question.isRequired && <Required>*</Required>}
                  </Label>
                  {question.controlType === 'TextBox' ? (
                    <Input
                      type="text"
                      value={localQuestionAnswers[qKey] || ''}
                      onChange={(e) => handleQuestionAnswerChange(question.questionId, occ, e.target.value)}
                      placeholder={`Please enter your answer${question.isRequired ? ' (required)' : ''}`}
                    />
                  ) : question.controlType === 'DropDownList' && question.answers ? (
                    <Select
                      value={localQuestionAnswers[qKey] || ''}
                      onChange={(e) => handleQuestionAnswerChange(question.questionId, occ, e.target.value)}
                    >
                      <option value="">Please select{question.isRequired ? ' (required)' : ''}</option>
                      {question.answers.map((answer, answerIndex) => (
                        <option key={`${question.questionId}-${answer.optionId}-${answerIndex}`} value={answer.answerValue}>
                          {answer.answerText}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      as="textarea"
                      rows={4}
                      value={localQuestionAnswers[qKey] || ''}
                      onChange={(e) => handleQuestionAnswerChange(question.questionId, occ, e.target.value)}
                      placeholder={`Please enter your answer${question.isRequired ? ' (required)' : ''}`}
                    />
                  )}
                </FormGroup>
              );
            });
          })()}
        </FormSection>
      )}

      {/* Participant Details Section for Grotto Retail Village */}
      {isGrottoRetailVillage && grottoParticipantDetails && grottoParticipantDetails.length > 0 && (
        <FormSection>
          <SectionHeader>
            Participant Information
          </SectionHeader>
          <p style={{ marginBottom: '1.5rem', color: '#666', fontSize: '0.95rem' }}>
            Please provide details for each participant
          </p>
          {grottoParticipantDetails.map((event, eventIndex) => (
            <div key={event.eventId || eventIndex} style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '1rem', color: '#2c3e50' }}>
                {event.eventName || event.description}
              </h4>
              {event.eventItemDetails && event.eventItemDetails.map((itemDetail, itemIndex) => (
                <div key={`${itemDetail.itemName}-${itemIndex}`} style={{ marginBottom: '1.5rem' }}>
                  {itemDetail.itemName && (
                    <h5 style={{ fontSize: '1.05rem', marginBottom: '0.75rem', opacity: 0.8 }}>
                      {itemDetail.itemName}
                    </h5>
                  )}
                  {itemDetail.participantDetails && itemDetail.participantDetails.map((participant, pIndex) => {
                    const participantId = `${event.eventId}-${itemIndex}-${pIndex}`;
                    return (
                      <div key={participantId} style={{ marginBottom: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                        <h6 style={{ fontSize: '0.95rem', marginBottom: '0.75rem', fontWeight: '600' }}>
                          {participant.isLeadParticipant ? 'Lead Participant' : `Participant ${pIndex + 1}`}
                        </h6>
                        <FormGrid>
                          <FormGroup>
                            <Label>
                              First Name <Required>*</Required>
                            </Label>
                            <Input
                              type="text"
                              autoCapitalize="words"
                              value={localParticipantAnswers[participantId]?.firstName || ''}
                              onChange={(e) => handleParticipantAnswerChange(participantId, 'firstName', e.target.value)}
                              placeholder="Enter first name (required)"
                            />
                          </FormGroup>
                          <FormGroup>
                            <Label>
                              Last Name <Required>*</Required>
                            </Label>
                            <Input
                              type="text"
                              autoCapitalize="words"
                              value={localParticipantAnswers[participantId]?.lastName || ''}
                              onChange={(e) => handleParticipantAnswerChange(participantId, 'lastName', e.target.value)}
                              placeholder="Enter last name (required)"
                            />
                          </FormGroup>
                        </FormGrid>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
        </FormSection>
      )}

      {/* Marketing Consent and Terms */}
      <FormSection style={{ opacity: isRegistered ? 0.6 : 1 }}>
        <SectionHeader>
          Communication Preferences {isRegistered && <span style={{ fontSize: '0.9rem', color: '#17a2b8', fontWeight: 'normal' }}>(Locked)</span>}
        </SectionHeader>
        
        <CheckboxContainer>
          <Checkbox
            id="marketing"
            type="checkbox"
            checked={marketingConsent}
            onChange={(e) => {
              setMarketingConsent(e.target.checked);
              // Save to customer details context
              handleInputChange('contactable', e.target.checked);
            }}
            disabled={isRegistered}
          />
          <CheckboxLabel htmlFor="marketing" disabled={isRegistered}>
            I would like to receive marketing communications about events, offers, and news from Belvoir Castle.
          </CheckboxLabel>
        </CheckboxContainer>

        <CheckboxContainer>
          <Checkbox
            id="terms"
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => {
              setTermsAccepted(e.target.checked);
              // Save to customer details context
              handleInputChange('termsAccepted', e.target.checked);
            }}
            disabled={isRegistered}
          />
          <CheckboxLabel htmlFor="terms" disabled={isRegistered}>
            I accept the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms and Conditions</a> <Required>*</Required>
          </CheckboxLabel>
        </CheckboxContainer>

        <div style={{ minHeight: '20px' }}>
          {errors.terms && <ErrorMessage>{errors.terms}</ErrorMessage>}
        </div>
      </FormSection>

      <ValidationSummary $isComplete={isFormComplete}>
        {isFormComplete ? (
          <>
            <SummaryTitle $isComplete={true}>
              All Required Information Complete!
            </SummaryTitle>
            <CompleteMessage>
              You can now continue to the next step
            </CompleteMessage>
          </>
        ) : (
          <>
            <SummaryTitle $isComplete={false}>
              Missing Required Information
            </SummaryTitle>
            <p style={{ margin: '0 0 1rem 0', color: '#856404', fontSize: '0.95rem' }}>
              Please complete the following fields to continue:
            </p>
            <MissingFieldsList>
              {missingFields.map((field, index) => (
                <MissingFieldItem key={`${field}-${index}`}>{field}</MissingFieldItem>
              ))}
            </MissingFieldsList>
          </>
        )}
      </ValidationSummary>

      <InfoBox>
        <InfoText>
          Please provide accurate contact information. We&apos;ll use this to send your booking confirmation and any important updates about your visit.
        </InfoText>
      </InfoBox>
      </form>
    </CustomerContainer>
  );
};

export default CustomerDetails;